import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, ContactShadows, useFBX } from "@react-three/drei";
import * as THREE from "three";
import { DecalGeometry } from "three/examples/jsm/geometries/DecalGeometry.js";
import { Stamp, Layers, Type, Trash2, Eye, EyeOff, Download, RotateCw, FlipHorizontal2, CloudRain, Palette, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { normalizeFbx } from "./modelUtils";

const ASSETS = "/vehicles";

function PainterCar({ paint, metallic, roughness, onCarClick, carRef, paintMode }) {
  const fbx = useFBX(`${ASSETS}/lynx_sm.fbx`);
  const model = useMemo(() => {
    const c = normalizeFbx(fbx, 4.8);
    return c;
  }, [fbx]);

  useEffect(() => { carRef.current = model; }, [model, carRef]);

  useEffect(() => {
    model.traverse((o) => {
      if (!o.isMesh) return;
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      mats.forEach((m) => {
        if (!m) return;
        const name = (m.name || o.name || "").toLowerCase();
        if (!/glass|window|tire|tyre|rubber|wheel|chrome|light|lens|interior|seat|dash/.test(name)) {
          m.color?.set(paint);
          if ("metalness" in m) m.metalness = metallic;
          if ("roughness" in m) m.roughness = roughness;
          m.needsUpdate = true;
        }
      });
    });
  }, [model, paint, metallic, roughness]);

  return (
    <primitive
      object={model}
      onClick={(e) => {
        if (!paintMode) return;
        e.stopPropagation();
        onCarClick(e);
      }}
    />
  );
}

function DecalLayer({ decal, carRef, register }) {
  useEffect(() => {
    const car = carRef.current;
    if (!car) return undefined;
    const parent = car.getObjectByProperty("uuid", decal.parentUuid);
    if (!parent) return undefined;
    const geo = new DecalGeometry(parent, new THREE.Vector3(...decal.position), new THREE.Euler(...decal.rotation), new THREE.Vector3(decal.size, decal.size, decal.size));
    const tex = new THREE.TextureLoader().load(decal.textureUrl);
    tex.colorSpace = THREE.SRGBColorSpace;
    const mat = new THREE.MeshStandardMaterial({ map: tex, transparent: true, depthTest: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -4, roughness: decal.roughness ?? 0.5, metalness: 0.1 });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.visible = decal.visible;
    parent.add(mesh);
    register(decal.id, mesh);
    return () => { parent.remove(mesh); geo.dispose(); mat.dispose(); tex.dispose(); };
  }, [decal, carRef, register]);
  return null;
}

const inputCls = "w-full bg-[#050505] border border-white/10 px-2.5 py-2 font-mono text-[11px] text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue";
const labelCls = "font-mono text-[8px] tracking-[0.25em] text-zinc-500 uppercase block mb-1";

export default function LiveryPainter({ me }) {
  const [paint, setPaint] = useState("#1a2a4a");
  const [metallic, setMetallic] = useState(0.4);
  const [roughness, setRoughness] = useState(0.35);
  const [weathering, setWeathering] = useState(0);
  const [stripe, setStripe] = useState("none");
  const [stripeColor, setStripeColor] = useState("#f5f5f5");
  const [decals, setDecals] = useState([]);
  const [pendingDecal, setPendingDecal] = useState(null); // textureUrl waiting to be stamped
  const [pendingText, setPendingText] = useState("");
  const [decalSize, setDecalSize] = useState(0.6);
  const [decalRot, setDecalRot] = useState(0);
  const [mirror, setMirror] = useState(true);
  const [skinName, setSkinName] = useState("VicPolice_HighwayPatrol_2026");
  const [baking, setBaking] = useState(false);
  const carRef = useRef(null);
  const decalMeshes = useRef({});

  const stamp = (e) => {
    if (!pendingDecal) return;
    const obj = e.object;
    const nWorld = e.face.normal.clone().transformDirection(obj.matrixWorld);
    const pWorld = e.point.clone();
    const add = (pointW, normalW, rot) => {
      const helper = new THREE.Object3D();
      helper.position.copy(pointW);
      helper.lookAt(pointW.clone().add(normalW));
      helper.rotateZ(rot);
      const qLocal = obj.getWorldQuaternion(new THREE.Quaternion()).invert().multiply(helper.quaternion);
      const pLocal = obj.worldToLocal(pointW.clone());
      const euler = new THREE.Euler().setFromQuaternion(qLocal);
      return {
        id: `d${Date.now()}${Math.random().toString(36).slice(2, 6)}`,
        textureUrl: pendingDecal, parentUuid: obj.uuid,
        position: [pLocal.x, pLocal.y, pLocal.z], rotation: [euler.x, euler.y, euler.z],
        size: decalSize, visible: true, roughness,
      };
    };
    const list = [add(pWorld, nWorld, decalRot)];
    if (mirror) list.push(add(new THREE.Vector3(-pWorld.x, pWorld.y, pWorld.z), new THREE.Vector3(-nWorld.x, nWorld.y, nWorld.z), -decalRot));
    setDecals((d) => [...d, ...list]);
    toast.success(mirror ? "Decal stamped + mirrored to the other side" : "Decal stamped");
  };

  const makeTextTexture = () => {
    if (pendingText.trim().length < 2) { toast.error("Type department text first"); return; }
    const cv = document.createElement("canvas");
    cv.width = 1024; cv.height = 256;
    const c = cv.getContext("2d");
    c.clearRect(0, 0, 1024, 256);
    c.font = "900 130px Arial";
    c.textAlign = "center"; c.textBaseline = "middle";
    c.fillStyle = "#f5f5f5";
    c.fillText(pendingText.toUpperCase(), 512, 128);
    setPendingDecal(cv.toDataURL("image/png"));
    toast.success("Text decal ready — click a body panel to stamp it");
  };

  const bake = async () => {
    setBaking(true);
    try {
      const S = 2048;
      const cv = document.createElement("canvas");
      cv.width = S; cv.height = S;
      const c = cv.getContext("2d");
      c.fillStyle = paint;
      c.fillRect(0, 0, S, S);
      // stripe masks
      c.fillStyle = stripeColor;
      if (stripe === "side") { c.fillRect(0, S * 0.42, S, S * 0.1); c.fillRect(0, S * 0.545, S, S * 0.02); }
      if (stripe === "hood") { c.beginPath(); c.moveTo(S * 0.3, 0); c.lineTo(S * 0.7, 0); c.lineTo(S * 0.62, S * 0.25); c.lineTo(S * 0.38, S * 0.25); c.fill(); }
      if (stripe === "checkers") { const s = S / 32; for (let i = 0; i < 32; i++) for (let j = 10; j < 14; j++) if ((i + j) % 2 === 0) c.fillRect(i * s, j * s, s, s); }
      // decals drawn at their stamped positions (panel-projection approximation)
      const imgs = await Promise.all(decals.filter((d) => d.visible).map((d) => new Promise((res) => {
        const img = new Image();
        img.onload = () => res({ d, img });
        img.onerror = () => res(null);
        img.src = d.textureUrl;
      })));
      imgs.filter(Boolean).forEach(({ d, img }) => {
        const px = S / 2 + d.position[0] * 620;
        const py = S / 2 - (d.position[1] - 1) * 620;
        const w = d.size * 620;
        c.save();
        c.translate(px, py);
        c.rotate(d.rotation[2]);
        c.drawImage(img, -w / 2, -w / 4, w, w / 2);
        c.restore();
      });
      // weathering / dirt
      if (weathering > 0) {
        for (let i = 0; i < weathering * 2600; i++) {
          c.fillStyle = `rgba(${20 + Math.random() * 40 | 0},${16 + Math.random() * 30 | 0},10,${0.05 + Math.random() * 0.12})`;
          const r = 2 + Math.random() * 14;
          c.beginPath();
          c.arc(Math.random() * S, S * 0.55 + Math.random() * S * 0.45, r, 0, 7);
          c.fill();
        }
      }
      const a = document.createElement("a");
      a.href = cv.toDataURL("image/png");
      a.download = `${skinName.replace(/\W+/g, "_")}.png`;
      a.click();
      const manifest = {
        vehicle_id: "veh_lynx_patrol", skin_name: skinName, author: me?.name || "Citizen",
        target_uv_slot: "Bodywork_Diffuse", texture_url: `skins/${skinName.replace(/\W+/g, "_")}.png`,
        roughness_override: +roughness.toFixed(2), metallic_override: +metallic.toFixed(2),
        engine_target: "UE5.1", resolution: 2048, layers: { stripe, stripe_color: stripeColor, decals: decals.filter((d) => d.visible).length, weathering },
      };
      const mb = new Blob([JSON.stringify(manifest, null, 2)], { type: "application/json" });
      const ma = document.createElement("a");
      ma.href = URL.createObjectURL(mb);
      ma.download = "skin_manifest.json";
      ma.click();
      toast.success("Baked 2048px livery PNG + skin_manifest.json exported");
    } finally { setBaking(false); }
  };

  return (
    <div data-testid="livery-painter" className="grid grid-cols-1 xl:grid-cols-[1fr_330px] gap-4">
      <div className="relative border border-white/10 bg-[#08080a] h-[620px] overflow-hidden">
        <Canvas camera={{ position: [5.2, 2.6, 6], fov: 42 }}>
          <color attach="background" args={["#08080a"]} />
          <fog attach="fog" args={["#08080a", 20, 55]} />
          <ambientLight intensity={0.55} />
          <directionalLight position={[6, 10, 4]} intensity={1.25} />
          <spotLight position={[-8, 9, -6]} intensity={50} angle={0.7} penumbra={0.8} color="#2E6BFF" />
          <Suspense fallback={null}>
            <PainterCar paint={paint} metallic={metallic} roughness={roughness} carRef={carRef} onCarClick={stamp} paintMode={!!pendingDecal} />
            {decals.map((d) => <DecalLayer key={d.id} decal={d} carRef={carRef} register={(id, mesh) => { decalMeshes.current[id] = mesh; }} />)}
          </Suspense>
          <ContactShadows position={[0, -0.02, 0]} opacity={0.7} scale={16} blur={2.2} />
          <gridHelper args={[40, 40, "#17171d", "#0e0e12"]} position={[0, -0.03, 0]} />
          <OrbitControls makeDefault enabled={!pendingDecal} minDistance={3} maxDistance={16} maxPolarAngle={Math.PI / 1.9} target={[0, 0.9, 0]} />
        </Canvas>
        <div className="absolute top-3 left-3 bg-black/70 backdrop-blur border border-white/10 px-3 py-2">
          <p className="font-mono text-[10px] text-zinc-100">Livery Painter — Lynx Interceptor</p>
          <p className="font-mono text-[8px] text-zinc-500">{pendingDecal ? "STAMP MODE — click a body panel (orbit paused)" : "Load a decal or text, then stamp it onto the body"}</p>
        </div>
        {pendingDecal && (
          <button data-testid="livery-cancel-stamp" onClick={() => setPendingDecal(null)} className="absolute top-3 right-3 font-mono text-[9px] uppercase border border-red-500/50 text-red-400 bg-black/70 px-3 py-2">Cancel stamp</button>
        )}
      </div>

      <div className="flex flex-col gap-4 max-h-[640px] overflow-y-auto pr-1">
        <div className="border border-white/10 bg-[#070707] p-4 flex flex-col gap-3">
          <p className="font-mono text-[9px] tracking-[0.3em] text-nc-blue uppercase flex items-center gap-2"><Palette className="w-3.5 h-3.5" /> Base Paint</p>
          <div className="flex items-center gap-3">
            <input data-testid="livery-paint" type="color" value={paint} onChange={(e) => setPaint(e.target.value)} className="w-12 h-9 bg-transparent border border-white/15 cursor-pointer" />
            <div className="flex-1">
              <div className="flex justify-between mb-1"><span className={labelCls}>Metallic</span><span className="font-mono text-[9px] text-nc-orange">{metallic.toFixed(2)}</span></div>
              <input data-testid="livery-metallic" type="range" min={0} max={1} step={0.05} value={metallic} onChange={(e) => setMetallic(+e.target.value)} className="w-full accent-[#FF6B1A]" />
            </div>
          </div>
          <div>
            <div className="flex justify-between mb-1"><span className={labelCls}>Roughness</span><span className="font-mono text-[9px] text-nc-orange">{roughness.toFixed(2)}</span></div>
            <input data-testid="livery-roughness" type="range" min={0} max={1} step={0.05} value={roughness} onChange={(e) => setRoughness(+e.target.value)} className="w-full accent-[#FF6B1A]" />
          </div>
          <div>
            <div className="flex justify-between mb-1"><span className={labelCls}>Weathering / dirt</span><span className="font-mono text-[9px] text-nc-orange">{Math.round(weathering * 100)}%</span></div>
            <input data-testid="livery-weathering" type="range" min={0} max={1} step={0.05} value={weathering} onChange={(e) => setWeathering(+e.target.value)} className="w-full accent-[#FF6B1A]" />
          </div>
        </div>

        <div className="border border-white/10 bg-[#070707] p-4 flex flex-col gap-3">
          <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-400 uppercase flex items-center gap-2"><Layers className="w-3.5 h-3.5 text-nc-orange" /> Striping Mask</p>
          <div className="grid grid-cols-4 gap-1.5">
            {["none", "side", "hood", "checkers"].map((s) => (
              <button key={s} data-testid={`stripe-${s}`} onClick={() => setStripe(s)} className={`font-mono text-[8px] uppercase border px-1 py-2 transition-colors ${stripe === s ? "border-nc-blue text-nc-blue bg-nc-blue/10" : "border-white/10 text-zinc-500"}`}>{s}</button>
            ))}
          </div>
          {stripe !== "none" && (
            <div className="flex items-center gap-2">
              <span className={labelCls}>Stripe color</span>
              <input data-testid="stripe-color" type="color" value={stripeColor} onChange={(e) => setStripeColor(e.target.value)} className="w-10 h-7 bg-transparent border border-white/15 cursor-pointer" />
            </div>
          )}
        </div>

        <div className="border border-white/10 bg-[#070707] p-4 flex flex-col gap-3">
          <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-400 uppercase flex items-center gap-2"><Stamp className="w-3.5 h-3.5 text-nc-orange" /> Decals & Text</p>
          <label className="font-mono text-[9px] uppercase border border-dashed border-white/20 text-zinc-400 hover:border-nc-blue/40 px-3 py-2.5 cursor-pointer transition-colors">
            Upload badge / crest (.png .svg)…
            <input data-testid="decal-upload" type="file" accept="image/png,image/svg+xml,image/jpeg" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (!f) return; setPendingDecal(URL.createObjectURL(f)); toast.success("Decal ready — click a body panel to stamp"); }} />
          </label>
          <div className="flex gap-2">
            <input data-testid="livery-text" className={inputCls} placeholder="POLICE // HIGHWAY PATROL" value={pendingText} maxLength={24} onChange={(e) => setPendingText(e.target.value)} />
            <button data-testid="livery-text-make" onClick={makeTextTexture} className="font-mono text-[9px] uppercase border border-nc-blue/50 text-nc-blue px-3 hover:bg-nc-blue/10 transition-colors flex items-center gap-1"><Type className="w-3.5 h-3.5" /> Make</button>
          </div>
          <div>
            <div className="flex justify-between mb-1"><span className={labelCls}>Stamp size</span><span className="font-mono text-[9px] text-nc-orange">{decalSize.toFixed(2)}m</span></div>
            <input data-testid="decal-size" type="range" min={0.2} max={1.8} step={0.05} value={decalSize} onChange={(e) => setDecalSize(+e.target.value)} className="w-full accent-[#FF6B1A]" />
          </div>
          <div>
            <div className="flex justify-between mb-1"><span className={labelCls}>Stamp rotation</span><span className="font-mono text-[9px] text-nc-orange">{Math.round(decalRot * 57.3)}°</span></div>
            <input data-testid="decal-rot" type="range" min={-3.14} max={3.14} step={0.05} value={decalRot} onChange={(e) => setDecalRot(+e.target.value)} className="w-full accent-[#FF6B1A]" />
          </div>
          <button data-testid="decal-mirror" onClick={() => setMirror((m) => !m)} className={`font-mono text-[9px] uppercase tracking-[0.15em] border px-3 py-2.5 transition-colors flex items-center gap-2 ${mirror ? "border-nc-orange text-nc-orange bg-nc-orange/10" : "border-white/15 text-zinc-400"}`}>
            <FlipHorizontal2 className="w-3.5 h-3.5" /> {mirror ? "Mirror L→R doors: ON" : "Mirror L→R doors: OFF"}
          </button>
          <div data-testid="decal-list" className="flex flex-col gap-1">
            {decals.map((d, i) => (
              <div key={d.id} className="flex items-center gap-2 border border-white/10 px-2 py-1.5">
                <span className="font-mono text-[9px] text-zinc-300 flex-1">Decal {i + 1}</span>
                <button data-testid={`decal-vis-${i}`} onClick={() => { const m = decalMeshes.current[d.id]; if (m) m.visible = !m.visible; setDecals((list) => list.map((x) => x.id === d.id ? { ...x, visible: !x.visible } : x)); }} className="text-zinc-500 hover:text-zinc-100">
                  {d.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                </button>
                <button data-testid={`decal-del-${i}`} onClick={() => setDecals((list) => list.filter((x) => x.id !== d.id))} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-nc-blue/30 bg-nc-blue/5 p-4 flex flex-col gap-3">
          <p className="font-mono text-[9px] tracking-[0.3em] text-nc-blue uppercase flex items-center gap-2"><Download className="w-3.5 h-3.5" /> Bake & Export</p>
          <div><span className={labelCls}>Skin name</span><input data-testid="skin-name" className={inputCls} value={skinName} maxLength={60} onChange={(e) => setSkinName(e.target.value)} /></div>
          <button data-testid="livery-bake" disabled={baking} onClick={bake} className="font-mono text-[10px] tracking-[0.2em] uppercase bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-50 text-white px-4 py-3 transition-colors flex items-center justify-center gap-2">
            {baking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Bake 2048px UV + Manifest
          </button>
          <p className="font-mono text-[8px] text-zinc-500 leading-relaxed">Exports the baked diffuse PNG plus skin_manifest.json (roughness/metallic overrides included) — drop both into NewCitizen/Content/Mods/Skins/ and the engine assigns it at load.</p>
        </div>
      </div>
    </div>
  );
}
